import { RULE_KINDS } from '@/shared/api/generated/rules';
import type { RuleKind } from '@/shared/api/generated/rules';

/* What each rule kind needs before it can be published, and how to ask for it.
 *
 * **This exists because four of the nine kinds could not be published at all.** The publish form sent
 * `parameters: {}` unconditionally, so `pattern`, `accepted_values`, `range`, `freshness`,
 * `custom_sql` and `referential_integrity` were refused by the server with their own message —
 * *"pattern requires parameter(s) pattern; a rule that cannot be executed must not be publishable"* —
 * and the form offered no field to satisfy it. The guard was right and the UI had no way past it.
 *
 * **Mirrored from `_REQUIRED_PARAMETERS` in `core/rules/domain/rule.py`, and the mirror is checked.**
 * `parameters.test.ts` asserts this table is total over `RULE_KINDS`, so a kind added to the contract
 * and forgotten here fails a test rather than shipping a form that cannot publish it. What it cannot
 * check is the server's *content*: this file is a second statement of the same requirement, and the
 * server remains the authority — a mismatch shows up as a refusal the form has no field for, which is
 * exactly the defect this replaces. Keeping them in step is a manual duty, and the test is what makes
 * forgetting loud rather than silent.
 */

/** How to render one parameter's input. `text` is the default; the others change validation only. */
export type ParameterInput = 'text' | 'number' | 'list' | 'sql';

export interface ParameterField {
  /** The key the server expects in `expression.parameters`. Not a display name. */
  readonly name: string;
  readonly input: ParameterInput;
  /** Copy keys, so no prose lives in a component. */
  readonly labelKey: string;
  readonly hintKey: string;
  readonly placeholderKey: string;
}

export interface KindShape {
  /** The parameters the server refuses a version without. */
  readonly parameters: readonly ParameterField[];
  /** Whether a version of this kind needs at least one target column.
   *
   * `row_count_anomaly` is the one kind that does not: its subject is the *relation* — it detects an
   * empty or collapsed table — so demanding a column would ask for something the check never reads.
   */
  readonly needsColumns: boolean;
  /** Copy key for what this kind measures, shown under the kind picker and beside a binding. */
  readonly measuresKey: string;
}

function field(name: string, input: ParameterInput, kind: RuleKind): ParameterField {
  return {
    name,
    input,
    labelKey: `rules.param.${kind}.${name}.label`,
    hintKey: `rules.param.${kind}.${name}.hint`,
    placeholderKey: `rules.param.${kind}.${name}.placeholder`,
  };
}

/* Keyed on `RuleKind` rather than branched on, which is `lumiq-arch` invariant 7's shape one layer
 * out: a mapping is the extension point, and a `switch` here would be the thing that gets a `default`
 * the day a tenth kind lands. */
export const KIND_SHAPES: Readonly<Record<RuleKind, KindShape>> = {
  not_null: {
    parameters: [],
    needsColumns: true,
    measuresKey: 'rules.measures.not_null',
  },
  unique: {
    parameters: [],
    needsColumns: true,
    measuresKey: 'rules.measures.unique',
  },
  accepted_values: {
    parameters: [field('values', 'list', 'accepted_values')],
    needsColumns: true,
    measuresKey: 'rules.measures.accepted_values',
  },
  range: {
    /* Two parameters, and the server checks their *relationship* as well as their type: an inverted
     * range matches nothing, so every row violates it — which is a broken rule rather than a failing
     * dataset, and the two must not look the same on a dashboard. `validate` below catches it here so
     * the author sees it beside the field rather than as a refusal after submitting. */
    parameters: [field('min', 'number', 'range'), field('max', 'number', 'range')],
    needsColumns: true,
    measuresKey: 'rules.measures.range',
  },
  pattern: {
    parameters: [field('pattern', 'text', 'pattern')],
    needsColumns: true,
    measuresKey: 'rules.measures.pattern',
  },
  freshness: {
    parameters: [field('max_age_hours', 'number', 'freshness')],
    needsColumns: true,
    measuresKey: 'rules.measures.freshness',
  },
  row_count_anomaly: {
    parameters: [],
    /* The only kind whose subject is the relation rather than a column. */
    needsColumns: false,
    measuresKey: 'rules.measures.row_count_anomaly',
  },
  referential_integrity: {
    parameters: [field('references', 'text', 'referential_integrity')],
    needsColumns: true,
    measuresKey: 'rules.measures.referential_integrity',
  },
  custom_sql: {
    parameters: [field('sql', 'sql', 'custom_sql')],
    needsColumns: false,
    measuresKey: 'rules.measures.custom_sql',
  },
};

/** Kinds the author form offers, and the one it does not.
 *
 * **`custom_sql` is offered and marked, rather than hidden.** It runs SQL the author writes against a
 * customer's production database with the connection's privileges, and this platform refuses it
 * everywhere it would be *derived*: `core.ai.rulegen`'s `PROPOSABLE_KINDS` excludes it at three
 * layers, and `lumiq-engine`'s `ENGINE_KINDS` refuses it by name because on a cluster it would run
 * with the cluster's privileges rather than a connection's.
 *
 * None of that is an argument for hiding it from a human author — it is the escape hatch the kind
 * exists to be, the API accepts it, and a kind the server allows but the form omits is a capability
 * that appears broken. It is last in the list and its own copy says what it costs.
 */
export const AUTHORABLE_KINDS: readonly RuleKind[] = [
  ...RULE_KINDS.filter((kind) => kind !== 'custom_sql'),
  'custom_sql',
];

/** Which parameter values are missing or malformed, keyed by parameter name.
 *
 * **Checked here as well as on the server, and the server is still the authority.** The point is
 * *where the author reads the message*: the range relationship in particular is refused with a clear
 * sentence by the domain, and a form that only learns it from a failed submit makes an author fix one
 * field at a time. An empty map means this form has nothing more to say, never that the server will
 * accept it.
 */
export function invalidParameters(
  kind: RuleKind,
  values: Readonly<Record<string, string>>,
): ReadonlyMap<string, string> {
  const problems = new Map<string, string>();

  for (const parameter of KIND_SHAPES[kind].parameters) {
    const raw = (values[parameter.name] ?? '').trim();
    if (raw === '') {
      problems.set(parameter.name, 'required');
      continue;
    }
    if (parameter.input === 'number' && !Number.isFinite(Number(raw))) {
      problems.set(parameter.name, 'notNumeric');
    }
  }

  if (kind === 'range' && problems.size === 0) {
    const low = Number((values['min'] ?? '').trim());
    const high = Number((values['max'] ?? '').trim());
    /* The domain's own words: "an inverted range matches nothing, so every row violates it. That is
     * not a failing dataset, it is a broken rule, and the two must not look the same on a
     * dashboard." Reported on `min`, because that is the field an author changes to fix it. */
    if (low > high) problems.set('min', 'inverted');
  }

  if (kind === 'referential_integrity' && !problems.has('references')) {
    /* `sql_builders` refuses a two-part value outright — it is "ambiguous about whether the middle
     * name is a schema or a table" — so three parts is the requirement rather than a convention. */
    const parts = (values['references'] ?? '').trim().split('.');
    if (parts.length !== 3 || parts.some((part) => part === '')) {
      problems.set('references', 'notThreePart');
    }
  }

  return problems;
}
